import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../utils/apiConfig';
import { LINKS } from '../constants/links';
import { setToken as persistToken, setUser as persistUser, clearAuthData } from '../utils/auth';
import { isNativeApp } from '../utils/platform';
import SmileLoader from './SmileLoader';
import '../styles/Login.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const ISO_CUBE = `${PUBLIC_URL}/iso-cube-clear.png`;
const ISO_PYRAMID = `${PUBLIC_URL}/iso-pyramid-clear.png`;

const AUTH_ICON = (name) => `${PUBLIC_URL}/auth/${name}${name.includes('?') ? '&' : '?'}v=7`;

/** Как в ЭО: телефон + кольцо из 3 иконок (templates/includes/auth_faces.html). */
const AUTH_FACE_ICONS = [
  'auth-icon-homework.webp',
  'auth-icon-pencil.webp',
  'auth-icon-laptop.webp',
];

function loadAuthIcon(src) {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    img.onload = () => {
      if (typeof img.decode === 'function') img.decode().then(finish, finish);
      else finish();
    };
    img.onerror = finish;
    img.src = src;
    if (img.complete) {
      if (img.naturalWidth && typeof img.decode === 'function') img.decode().then(finish, finish);
      else finish();
    }
  });
}

const AuthFaces = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const timer = window.setTimeout(() => {
      if (alive) setReady(true);
    }, 900);
    Promise.all(AUTH_FACE_ICONS.map((name) => loadAuthIcon(AUTH_ICON(name)))).then(() => {
      if (!alive) return;
      window.clearTimeout(timer);
      setReady(true);
    });
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className={`auth-faces${ready ? ' is-ready' : ''}`} aria-hidden="true">
      <div className="auth-faces__phone">
        <span className="auth-faces__island" />
        <div className="auth-faces__screen">
          <div className="auth-faces__note">
            <img className="auth-faces__note-ava" src={AUTH_ICON('auth-icon-clock.webp')} alt="" />
            <span className="auth-faces__note-copy">
              <span className="auth-faces__note-head"><strong>1 пара</strong><time>сейчас</time></span>
              <span className="auth-faces__note-text">Математика · ауд. 214</span>
            </span>
          </div>
          <div className="auth-faces__note">
            <img className="auth-faces__note-ava" src={AUTH_ICON('auth-icon-bell.webp')} alt="" />
            <span className="auth-faces__note-copy">
              <span className="auth-faces__note-head"><strong>Замена</strong><time>2м</time></span>
              <span className="auth-faces__note-text">Физика перенесена</span>
            </span>
          </div>
          <div className="auth-faces__list">
            <div className="auth-faces__item is-now"><span>1</span><b>Алгебра</b><em>8:00</em></div>
            <div className="auth-faces__item"><span>2</span><b>Физика</b><em>9:40</em></div>
            <div className="auth-faces__item"><span>3</span><b>История</b><em>11:20</em></div>
          </div>
        </div>
      </div>
      <div className="auth-faces__ring">
        <span className="auth-faces__ava">
          <img src={AUTH_ICON('auth-icon-homework.webp')} alt="" decoding="async" />
        </span>
        <span className="auth-faces__ava">
          <img src={AUTH_ICON('auth-icon-pencil.webp')} alt="" decoding="async" />
        </span>
        <span className="auth-faces__ava">
          <img src={AUTH_ICON('auth-icon-laptop.webp')} alt="" decoding="async" />
        </span>
      </div>
    </div>
  );
};

const AuthError = ({ text }) => (
  <div className="auth-lite__error" role="alert">
    <span className="auth-lite__error-ava" aria-hidden="true">
      <span className="auth-lite__error-bang" />
    </span>
    <span className="auth-lite__error-copy">
      <span className="auth-lite__error-head">
        <strong>Расписание</strong>
        <time>сейчас</time>
      </span>
      <span className="auth-lite__error-text">{text}</span>
    </span>
  </div>
);

const AuthEye = ({ on, onToggle }) => (
  <button
    type="button"
    className={`auth-lite__eye${on ? ' is-on' : ''}`}
    onClick={onToggle}
    aria-pressed={on}
    aria-label={on ? 'Скрыть пароль' : 'Показать пароль'}
  >
    <span className="auth-lite__eye-swap" aria-hidden="true">
      <i className="bi bi-eye"></i>
      <i className="bi bi-eye-slash"></i>
    </span>
  </button>
);

const Login = ({ setToken, setUser }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const navigate = useNavigate();

  const STORAGE_KEYS = {
    TOKEN: 'token',
    USER: 'user',
    SKIP_AUTOLOGIN: 'kkt_skip_autologin',
  };

  const checkMaintenance = async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 4000) : null;
    try {
      const response = await fetch(apiUrl('/api/admin/maintenance/status'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller?.signal,
      });
      if (!response.ok) return false;
      const data = await response.json();
      setMaintenanceMode(Boolean(data.maintenance_mode));
      return Boolean(data.maintenance_mode);
    } catch (err) {
      console.log('Ошибка проверки обслуживания:', err);
      return false;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  useEffect(() => {
    checkMaintenance();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const inMaintenance = await checkMaintenance();
      if (inMaintenance) {
        setError('Сервер находится на техническом обслуживании. Попробуйте позже.');
        return;
      }

      const response = await axios.post(
        apiUrl('/alogin'),
        { username, password },
        { timeout: 12000 }
      );

      if (!response.data || (!response.data.access_token && !response.data.token)) {
        throw new Error('Неверный формат ответа сервера');
      }

      const accessToken = response.data.access_token || response.data.token;
      persistToken(accessToken, remember);
      setToken(accessToken);

      const userResponse = await axios.get(apiUrl('/users/me'), {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 8000,
      });

      const userData = userResponse.data;
      persistUser(userData, remember);
      setUser(userData);
      try { sessionStorage.removeItem(STORAGE_KEYS.SKIP_AUTOLOGIN); } catch { /* ignore */ }
      navigate('/schedule');
    } catch (err) {
      console.error('Ошибка авторизации:', err);

      let errorMessage = 'Неверный логин или пароль. Оба поля могут быть чувствительны к регистру.';
      const msg = err.message || '';
      const status = err.response?.status;

      if (err.code === 'ECONNABORTED' || msg.includes('timeout')) {
        errorMessage = 'Сервер не отвечает. Попробуйте ещё раз.';
      } else if (err.message === 'Network Error' || msg.includes('Failed to fetch') || !err.response) {
        const onLocal =
          typeof window !== 'undefined' &&
          ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
        errorMessage = onLocal
          ? 'Нет связи с ЭО. В репозитории ЭО запустите: python manage.py runserver 127.0.0.1:8082 — логин тот же, что в ЭО.'
          : 'Нет связи с ЭО. Логин и пароль те же, что на e-learn.kktbel.ru. Проверьте сеть и попробуйте снова.';
      } else if (status === 502 || status === 504) {
        errorMessage = 'Сервер временно недоступен. Попробуйте позже.';
      } else if (msg.includes('Неверный формат ответа')) {
        errorMessage = 'Ошибка сервера';
      } else if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : 'Неверный логин или пароль';
      }

      setError(errorMessage);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const shell = (panelContent) => (
    <div className="kkt-shell">
      <div className="kkt-bg" aria-hidden="true">
        <div className="kkt-bg__blob kkt-bg__blob--1"></div>
        <div className="kkt-bg__blob kkt-bg__blob--2"></div>
        <div className="kkt-bg__blob kkt-bg__blob--3"></div>
        <img className="kkt-bg__iso kkt-bg__iso--cube-1" src={ISO_CUBE} alt="" decoding="async" />
        <img className="kkt-bg__iso kkt-bg__iso--pyramid-1" src={ISO_PYRAMID} alt="" decoding="async" />
        <img className="kkt-bg__iso kkt-bg__iso--cube-2" src={ISO_CUBE} alt="" decoding="async" />
        <img className="kkt-bg__iso kkt-bg__iso--pyramid-2" src={ISO_PYRAMID} alt="" decoding="async" />
        <img className="kkt-bg__iso kkt-bg__iso--cube-3" src={ISO_CUBE} alt="" decoding="async" />
      </div>

      {isNativeApp() ? null : (
        <a className="auth-lite__home" href="/#/">
          На главную
        </a>
      )}

      <main className="auth-lite__page">
        <section className="auth-lite__card">
          {panelContent}
        </section>
      </main>
    </div>
  );

  if (maintenanceMode) {
    return shell(
      <>
        <AuthFaces />
        <h1 className="auth-lite__title">Обслуживание</h1>
        <p className="auth-lite__hint">Сервер временно недоступен</p>
        <div className="auth-status-panel">
          <div className="maintenance-icon">
            <i className="bi bi-tools"></i>
          </div>
          <p>Проводятся технические работы.</p>
          <p className="muted">Попробуйте войти позже</p>
        </div>
      </>
    );
  }

  return shell(
    resetOpen ? (
      <>
        <img
          className="auth-lite__mark auth-lite__mark--question"
          src={AUTH_ICON('auth-icon-question.webp')}
          width="128"
          height="128"
          alt=""
        />
        <h1 className="auth-lite__title">Забыли пароль?</h1>
        <p className="auth-lite__hint">
          Восстановление через бота ВК. Если аккаунт привязан — откройте бота и нажмите «Пароль».
          Если привязки ещё нет — войдите, когда вспомните пароль, и привяжите ВК в Настройки → Пароль.
        </p>
        <div className="auth-lite__reset-actions">
          <a
            className="auth-lite__submit"
            href={LINKS.vkCommunityDm}
            target="_blank"
            rel="noopener noreferrer"
          >
            Открыть бота ВК
          </a>
          <a className="auth-lite__skip" href={`${LINKS.eo}/password-reset/`}>
            Подробнее
          </a>
          <button type="button" className="auth-lite__skip" onClick={() => setResetOpen(false)}>
            Ко входу
          </button>
        </div>
      </>
    ) : (
      <>
        <AuthFaces />
        <h1 className="auth-lite__title">Расписание</h1>

        <form onSubmit={handleSubmit} className={`auth-lite__form${error ? ' auth-lite__form--bad' : ''}`} noValidate>
          {error ? <AuthError text={error} /> : null}

          <div className="auth-lite__field">
            <div className="auth-lite__control">
              <input
                id="id_username"
                type="text"
                className={`auth-lite__input${error ? ' is-invalid' : ''}`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                placeholder="Логин"
                aria-label="Логин"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
            </div>
          </div>

          <div className="auth-lite__field">
            <div className="auth-lite__control">
              <input
                id="id_password"
                type={showPassword ? 'text' : 'password'}
                className={`auth-lite__input auth-lite__input--pass${error ? ' is-invalid' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="Пароль"
                aria-label="Пароль"
                autoComplete="current-password"
                required
              />
              <AuthEye on={showPassword} onToggle={() => setShowPassword((v) => !v)} />
            </div>
          </div>

          <label className="auth-lite__remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={loading}
            />
            <span>Запомнить меня</span>
          </label>

          <button
            type="submit"
            className="auth-lite__submit"
            disabled={loading || maintenanceMode}
          >
            {loading ? (
              <span className="auth-lite__submit-busy">
                <SmileLoader size="xs" />
                <span>Вход…</span>
              </span>
            ) : (
              <span className="auth-lite__submit-idle">Войти</span>
            )}
          </button>
        </form>

        <button type="button" className="auth-lite__skip" onClick={() => setResetOpen(true)} disabled={loading}>
          Забыли пароль?
        </button>
      </>
    )
  );
};

export default Login;
